"use client"

import ButtonWithArrow from "@/ui/common/bit/ButtonWithArrow"
import { usePolicyFormValidationSchema } from "@/utils/Form/PolicyFormValidations"
import { handleNumericInput } from "@/utils/utils"
import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { mediaQueries } from "ui"
import { colors, sizes } from "ui/theme/theme"

// Styled components
const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${sizes.baseDouble};
`

const StyledFormGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${sizes.baseDouble};

  ${mediaQueries.desktopM} {
    grid-template-columns: 1fr 1fr;
    column-gap: 20px;
    row-gap: 20px;
  }
`

const StyledFormField = styled(TextField)`
  width: 100%;

  /* The following styles are already defined in theme.ts MuiTextField styleOverrides, so we're removing them */
`

const StyledButton = styled(ButtonWithArrow)`
  margin: 0 auto;
`

const StyledCheckboxLabel = styled(FormControlLabel)`
  margin: 0;
  .MuiTypography-root {
    font-size: 14px;
    color: ${colors.black}; /* Ensure text is always black */
  }
`

const StyledCheckboxContainer = styled(Box)`
  padding-top: 25px;
  padding-bottom: 24px;

  .MuiTypography-caption {
    margin-left: 0;
    font-size: 12px;
  }

  /* Error styling applies only to the checkbox icon */
  .error {
    .MuiCheckbox-root {
      color: ${colors.red}; /* This affects only the checkbox color */
    }
    /* The label text stays black because of the rule in StyledCheckboxLabel */
  }

  ${mediaQueries.desktopM} {
    padding-bottom: 48.5px;
  }
`

const StyledCheckBoxErrorMessage = styled(Typography)`
  display: block;
`

// Form types
export interface FormData {
  policyNumber: string
  email: string
  licensePlate: string
  phoneNumber: string
  id: string
  agreeTerms: boolean
}

// Policy Form Component Props
export interface PolicyFormProps {
  data: any
  onSubmit: (formData: FormData) => void
  isSubmitting: boolean
}

const PolicyForm = ({ data, onSubmit, isSubmitting }: PolicyFormProps) => {
  const [formSubmitAttempt, setFormSubmitAttempt] = useState(0)
  const validationSchema = usePolicyFormValidationSchema()

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      policyNumber: "",
      email: "",
      licensePlate: "",
      phoneNumber: "",
      id: "",
      agreeTerms: false,
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange", // Validate on change for better user experience
  })

  // Enhanced submit handler with debugging
  const submitForm = (formData: FormData) => {
    onSubmit(formData)
  }

  // Handler for form errors to help debug submission issues
  const onError = (errors: any) => {
    setFormSubmitAttempt((prev) => prev + 1)
  }

  return (
    <StyledForm
      onSubmit={handleSubmit(submitForm, onError)}
      noValidate // Let React Hook Form handle validation
    >
      <StyledFormGrid>
        <Controller
          name="policyNumber"
          control={control}
          render={({ field }) => (
            <StyledFormField
              {...field}
              label="מספר פוליסה"
              variant="outlined"
              error={!!errors.policyNumber}
              helperText={errors.policyNumber?.message}
              disabled={isSubmitting}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 20,
              }}
              onInput={handleNumericInput}
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <StyledFormField
              {...field}
              label="אימייל"
              variant="outlined"
              type="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={isSubmitting}
            />
          )}
        />

        <Controller
          name="licensePlate"
          control={control}
          render={({ field }) => (
            <StyledFormField
              {...field}
              label="מספר רישוי"
              variant="outlined"
              error={!!errors.licensePlate}
              helperText={errors.licensePlate?.message}
              disabled={isSubmitting}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 10,
              }}
              onInput={handleNumericInput}
            />
          )}
        />

        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <StyledFormField
              {...field}
              label="מספר טלפון"
              variant="outlined"
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
              disabled={isSubmitting}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 10,
              }}
              onInput={handleNumericInput}
            />
          )}
        />

        <Controller
          name="id"
          control={control}
          render={({ field }) => (
            <StyledFormField
              {...field}
              label="מספר תעודת זהות"
              variant="outlined"
              error={!!errors.id}
              helperText={errors.id?.message}
              disabled={isSubmitting}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 9,
              }}
              onInput={handleNumericInput}
            />
          )}
        />
      </StyledFormGrid>

      <StyledCheckboxContainer>
        <Controller
          name="agreeTerms"
          control={control}
          render={({ field }) => (
            <div className={errors.agreeTerms ? "error" : ""}>
              <StyledCheckboxLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    disabled={isSubmitting}
                    sx={{
                      "&.Mui-error, &.Mui-error + .MuiFormControlLabel-label": {
                        color: errors.agreeTerms ? colors.red : undefined,
                      },
                      "& + .MuiFormControlLabel-label": {
                        color: colors.black /* Keep label text black */,
                      },
                    }}
                  />
                }
                label={data.checkboxText?.value || "אני מאשר/ת את תנאי השימוש"}
              />
            </div>
          )}
        />
        {errors.agreeTerms && (
          <StyledCheckBoxErrorMessage color="error" variant="caption">
            {errors.agreeTerms.message}
          </StyledCheckBoxErrorMessage>
        )}
      </StyledCheckboxContainer>

      <StyledButton type="submit" disabled={isSubmitting}>
        {isSubmitting ? "שולח..." : data.buttonText.value}
      </StyledButton>
    </StyledForm>
  )
}

export default PolicyForm
